import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from "@mui/styles";
import CloseIcon from '@mui/icons-material/Close';
import {
  AppBar,
  Box,
  Button,
  FormControl,
  IconButton, List,
  Paper,
  Stack, Toolbar, Tooltip,
  Typography,
  Grid
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import ColorConstants from '../../../defaults/ColorConstants';
import FieldNames from '../../../defaults/FieldNames.json';
import ErrorService from '../../../services/ErrorService';
import { queryAllSummaryFeatureTables } from '../../../utils/SummaryHelper';
import { getBaseFilterString, getBaseFuelsString, getBasePostFireString } from '../../../utils/QueryHelper';
import './ReportingDialog.css';
import SummariesList from '../SummariesList/SummariesList';
import SummarySelectionMenu from '../SummarySelectionMenu/SummarySelectionMenu';
import { ComponentToPrint } from '../ComponentToPrint/ComponentToPrint';
import HelpIcon from "@mui/icons-material/Help";
import { useDispatch } from 'react-redux';
import { setShowMap } from '../../../redux/slices/displayStateSlice';

const theme = createTheme({
  overrides: {
    
  },
});

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  const finalWidth = width - 20;
  return {
    finalWidth,
    height
  };
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}

const BootstrapButton = styled(Button)({
  boxShadow: '0 0 0 0.2rem rgba(0,123,255,.5)',
  border: '1px solid',
  textTransform: 'uppercase',
  color: ColorConstants.BlueAlien,
  backgroundColor: ColorConstants.WhiteAlien,
  
  '&:hover': {
    fontWeight: 'bold',
    color: ColorConstants.BlueAlien,
    backgroundColor: ColorConstants.WhiteAlien,
    borderColor: ColorConstants.WhiteAlien,
  },
  '&:active': {
    backgroundColor: ColorConstants.WhiteAlien,
    borderColor: ColorConstants.WhiteAlien,
  },
  '&:focus': {
    fontWeight: 'bold',
    color: ColorConstants.BlueAlien,
    backgroundColor: ColorConstants.WhiteAlien,
    borderColor: ColorConstants.WhiteAlien,
  },
  '&:disabled': {
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
    borderColor: 'rgba(0, 0, 0, 0.12)',
  }
});

export default function ReportingDialog({
  currentUserInfo,
  userRoleInfo,
  headerheight
}) {
  const dispatch = useDispatch();

  const componentRef = useRef();
  
  const [selectedSummaries, setSelectedSummaries] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);
  const { height, finalWidth } = useWindowDimensions();
  const [activityFundingFeatures, setActivityFundingFeatures] = useState([]);
  const [treatmentFundingFeatures, setTreatmentFundingFeatures] = useState([]);
  const [actualActivityFundingFeatures, setActualActivityFundingFeatures] = useState([]);
  const [actualTreatmentFundingFeatures, setActualTreatmentFundingFeatures] = useState([]);
  const [estimatedCombinedFundingFeatures, setEstimatedCombinedFundingFeatures] = useState([]);
  const [actualCombinedFundingFeatures, setActualCombinedFundingFeatures] = useState([]);
  const [activityFeatures, setActivityFeatures] = useState([]);
  const [treatmentFeatures, setTreatmentFeatures] = useState([]);
  const [actualActivityFeatures, setActualActivityFeatures] = useState([]);
  const [actualTreatmentFeatures, setActualTreatmentFeatures] = useState([]);
  const [estimatedCombinedFeatures, setEstimatedCombinedFeatures] = useState([]);
  const [actualCombinedFeatures, setActualCombinedFeatures] = useState([]);
  const definitionExpression = useSelector((state) => state.filter.definitionExpression);
  const summaryOptions = FieldNames.summaryOptions;
  const summaryClasses = FieldNames.summaryClasses;
  const [isActivitiesLoaded, setIsActivitiesLoaded] = useState(false);
  const [isTreatmentsLoaded, setIsTreatmentsLoaded] = useState(false);
  const [isActActivitiesLoaded, setIsActActivitiesLoaded] = useState(false);
  const [isActTreatmentsLoaded, setIsActTreatmentsLoaded] = useState(false);
  const [isActivityFundingLoaded, setIsActivityFundingLoaded] = useState(false);
  const [isTreatmentFundingLoaded, setIsTreatmentFundingLoaded] = useState(false);
  const [isActActivityFundingLoaded, setIsActActivityFundingLoaded] = useState(false);
  const [isActTreatmentFundingLoaded, setIsActTreatmentFundingLoaded] = useState(false);
  const [isCheckboxChanged, setIsCheckboxChanged] = useState(false);
  const navigate = useNavigate();
  const [summaryOptionItems, setSummaryOptionItems] = useState([]);
  const [summaryClassItems, setSummaryClassItems] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);

  const useStyles = makeStyles({
    underline: {
      "&&&:before": {
        borderBottom: "2px solid red",
      },
      "&&:after": {
        borderBottom: "2px solid red",
      },
    },
    condensedFormControl: {
      margin: "5px 15px 5px 15px",
      minWidth: 120,
      width: "90%",
    },
    formControl: {
      margin: "10px 15px 10px 15px",
      minWidth: 120,
      width: "100%",
    },
    formControlHeader: {
      margin: "0px 15px 0px 15px",
      minWidth: 120,
      width: "90%",
    },
    snackBarStyle: {
      top: "120px !important",
    },
    root: {
      flexGrow: 1,
    },
  });
  const classes = useStyles();

  useEffect(() => {
    dispatch(setShowMap(false));
  }, [dispatch]);

  const handleClose = () => {
    dispatch(setShowMap(true));
    navigate("/home");
  };

  useEffect(() => {
    let finalSummaryOptions = [];
    let finalFundingChildren = [];
    let finalOwnershipChildren = [];
    if(summaryOptions?.length){
      summaryOptions.forEach((sumOption) => {
        if(sumOption === FieldNames.summaryOptionsFields.fundingSource || sumOption === FieldNames.summaryOptionsFields.fundingSourceProgram || sumOption === FieldNames.summaryOptionsFields.fundingSourceCategory ||
          sumOption === FieldNames.summaryOptionsFields.fundingSourceSubCategory || sumOption === FieldNames.summaryOptionsFields.fundingDepartment || sumOption === FieldNames.summaryOptionsFields.fundingAgency || 
          sumOption === FieldNames.summaryOptionsFields.fundingRegion || sumOption === FieldNames.summaryOptionsFields.fundingUnit || sumOption === FieldNames.summaryOptionsFields.fundingTribe || 
          sumOption === FieldNames.summaryOptionsFields.fundingSubUnit) 
        {
          let label;
          switch(sumOption){
            case FieldNames.summaryOptionsFields.fundingDepartment:
              label = FieldNames.summaryOptionsFieldNames.department;
              break;
            case FieldNames.summaryOptionsFields.fundingAgency:
              label = FieldNames.summaryOptionsFieldNames.agency;
              break;
            case FieldNames.summaryOptionsFields.fundingRegion:
              label = FieldNames.summaryOptionsFieldNames.region;
              break;
            case FieldNames.summaryOptionsFields.fundingUnit:
              label = FieldNames.summaryOptionsFieldNames.unit;
              break;
            case FieldNames.summaryOptionsFields.fundingTribe:
              label = FieldNames.summaryOptionsFieldNames.tribe;
              break;
            case FieldNames.summaryOptionsFields.fundingSubUnit:
              label = FieldNames.summaryOptionsFieldNames.subUnit;
              break;
            default:
              label = sumOption;
              break;
          }

          let obj = {
            id: sumOption,
            label: label
          }
          finalFundingChildren.push(obj);
        } else if(sumOption === FieldNames.summaryOptionsFields.department || sumOption === FieldNames.summaryOptionsFields.agency || sumOption === FieldNames.summaryOptionsFields.region || 
          sumOption === FieldNames.summaryOptionsFields.unit || sumOption === FieldNames.summaryOptionsFields.subUnit || sumOption === FieldNames.summaryOptionsFields.tribe || sumOption === FieldNames.summaryOptionsFields.state || 
          sumOption === FieldNames.summaryOptionsFields.county || sumOption === FieldNames.summaryOptionsFields.congressional) 
        {
          let label;
          switch(sumOption){
            case FieldNames.summaryOptionsFields.department:
              label = FieldNames.summaryOptionsFieldNames.department;
              break;
            case FieldNames.summaryOptionsFields.agency:
              label = FieldNames.summaryOptionsFieldNames.agency;
              break;
            case FieldNames.summaryOptionsFields.region:
              label = FieldNames.summaryOptionsFieldNames.region;
              break;
            case FieldNames.summaryOptionsFields.unit:
              label = FieldNames.summaryOptionsFieldNames.unit;
              break;
            case FieldNames.summaryOptionsFields.tribe:
              label = FieldNames.summaryOptionsFieldNames.tribe;
              break;
            case FieldNames.summaryOptionsFields.subUnit:
              label = FieldNames.summaryOptionsFieldNames.subUnit;
              break;
            default:
              label = sumOption;
              break;
          }
          let obj = {
            id: sumOption,
            label: label
          }
          finalOwnershipChildren.push(obj);
        } else {
          let obj = {
            id: sumOption,
            label: sumOption,
            children: []
          }
          finalSummaryOptions.push(obj)
        }
        
      })
      const funding = {
        id: FieldNames.summaryForChecking.funding,
        label: FieldNames.summaryOptionsFieldNames.funding,
        children: finalFundingChildren
      }
      finalSummaryOptions.push(funding);

      const ownership = {
        id: FieldNames.summaryForChecking.ownership,
        label: FieldNames.summaryOptionsFieldNames.ownership,
        children: finalOwnershipChildren
      }
      finalSummaryOptions.push(ownership);
      setSummaryOptionItems(finalSummaryOptions)
    }

    let finalSummaryClassOptions = [];
    if(summaryClasses?.length){
      summaryClasses.forEach((sumClassOption) => {
        let obj = {
          id: sumClassOption,
          label: sumClassOption,
          children: []
        }
        finalSummaryClassOptions.push(obj)
      })
      setSummaryClassItems(finalSummaryClassOptions);
    }
  }, [summaryOptions, summaryClasses])

  useEffect(() => {
    const getIsFundingInner = () => {
      let isFunding = false;
      if(selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingAgency) || selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingDepartment) || 
      selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingRegion) || selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingUnit) ||
      selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingTribe) || selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingSubUnit) || 
      selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingSource) || selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingSourceProgram) ||
      selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingSourceCategory) || selectedSummaries.includes(FieldNames.summaryOptionsFields.fundingSourceSubCategory)){
        isFunding = true;
      }
      return isFunding;
    }

    if(isLoading){
      let itemsLoaded = [];
      selectedClasses.forEach(sumClass => {
        if(sumClass === FieldNames.classTypesPlural.treatments){
          if(getIsFundingInner()){
            if(isTreatmentFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isTreatmentsLoaded){
              itemsLoaded.push(true);
            }
          }
        } else if(sumClass === FieldNames.classTypesPlural.activities){
          if(getIsFundingInner()){
            if(isActivityFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isActivitiesLoaded){
              itemsLoaded.push(true);
            }
          }
        } else if(sumClass === FieldNames.classTypesPlural.actualTreatments){
          if(getIsFundingInner()){
            if(isActTreatmentFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isActTreatmentsLoaded){
              itemsLoaded.push(true);
            }
          }
        } else if(sumClass === FieldNames.classTypesPlural.actualActivities){
          if(getIsFundingInner()){
            if(isActActivityFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isActActivitiesLoaded){
              itemsLoaded.push(true);
            }
          }
        } else if(sumClass === FieldNames.classTypes.combinedEstimated){
          if(getIsFundingInner()){
            if(isTreatmentFundingLoaded && isActivityFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isTreatmentsLoaded && isActivitiesLoaded){
              itemsLoaded.push(true);
            }
          }
        } else if(sumClass === FieldNames.classTypes.combinedActual){
          if(getIsFundingInner()){
            if(isActTreatmentFundingLoaded && isActActivityFundingLoaded){
              itemsLoaded.push(true);
            }
          } else {
            if(isActActivitiesLoaded && isActTreatmentsLoaded){
              itemsLoaded.push(true);
            }
          }
        } 
      });

      if(selectedClasses.length === itemsLoaded.length){
        if((selectedClasses.includes(FieldNames.classTypes.combinedActual) && (actualCombinedFeatures?.length || actualCombinedFundingFeatures?.length)) ||
          (selectedClasses.includes(FieldNames.classTypes.combinedEstimated) && (estimatedCombinedFeatures?.length || estimatedCombinedFundingFeatures?.length)) || 
          (selectedClasses.includes(FieldNames.classTypesPlural.treatments) && (isTreatmentsLoaded || isTreatmentFundingLoaded)) ||
          (selectedClasses.includes(FieldNames.classTypesPlural.activities) && (isActivitiesLoaded || isActivityFundingLoaded)) || 
          (selectedClasses.includes(FieldNames.classTypesPlural.actualTreatments) && (isActTreatmentsLoaded || isActTreatmentFundingLoaded)) ||
          (selectedClasses.includes(FieldNames.classTypesPlural.actualActivities) && (isActActivitiesLoaded || isActActivityFundingLoaded))){
          setIsLoading(false);
        } 
      }
    }

    
  }, [actualCombinedFundingFeatures, estimatedCombinedFundingFeatures, actualCombinedFeatures, estimatedCombinedFeatures, 
      isTreatmentsLoaded, isActivitiesLoaded, isActTreatmentsLoaded, isActActivitiesLoaded, isLoading, 
      isTreatmentFundingLoaded, isActivityFundingLoaded, isActTreatmentFundingLoaded, isActActivityFundingLoaded, 
      selectedSummaries, selectedClasses])

  const getIsTypeOfSummary = (selSummaries) => {
    let isFunding = false;
    let isNonFunding = false;
    selSummaries?.forEach(summary => {
      if(summary?.includes("Funding")){
        isFunding = true;
      }

      if(!summary?.includes("Funding")){
        isNonFunding = true;
      }
    })

    let obj = {
      isFunding: isFunding,
      isNonFunding: isNonFunding
    }
    return obj;
  }

  const settingFeatureValues = (featureLoaded, response, setFeature, setLoaded, classValue, features) => {
    let featureArray = features;
    if(!featureLoaded){
      featureArray = response.filter(x => x[FieldNames.featureFields.class] === classValue);
      if(featureArray?.length){
        setFeature(featureArray);
      }
      setLoaded(true);
    }
    return featureArray;
  }

  const gettingCombinedValues = (response, isTrtLoaded, isActLoaded, setTrtFeatures, setActFeatures, setIsTrtLoaded, setIsActLoaded, classTrtField, classActField, trtFeatures, actFeatures) => {
    let trtArray = [];
    let actArray = [];
    if(!isTrtLoaded){
      trtArray = settingFeatureValues(isTrtLoaded, response, setTrtFeatures, setIsTrtLoaded, classTrtField, trtFeatures);
    } else {
      trtArray = trtFeatures
    }

    if(!isActLoaded){
      actArray = settingFeatureValues(isActLoaded, response, setActFeatures, setIsActLoaded, classActField, actFeatures);
    } else {
      actArray = actFeatures
    }
    return trtArray.concat(actArray);
  }

  const handleGenerateSummaries = async (selClasses, selSummaries) =>
  {
    setIsCheckboxChanged(false);
    setIsLoading(true);

    if(selClasses?.length > 0){
      let finalArrayForCall = [];
      let finalFundingArrayForCall = [];
      selClasses.forEach((eaClass) => {
        switch(eaClass){
          case FieldNames.classTypesPlural.treatments:
            if(!finalArrayForCall.includes(FieldNames.classTypes.treatment) && !isTreatmentsLoaded){
                finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.treatment}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.treatment) && !isTreatmentFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.treatment}'`)
            }
            break;
          case FieldNames.classTypesPlural.activities:
            if(!finalArrayForCall.includes(FieldNames.classTypes.activity) && !isActivitiesLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.activity}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.activity) && !isActivityFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.activity}'`)
            }
            break;
          case FieldNames.classTypesPlural.actualTreatments:
            if(!finalArrayForCall.includes(FieldNames.classTypes.actualTreatment) && !isActTreatmentsLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualTreatment}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.actualTreatment) && !isActTreatmentFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualTreatment}'`)
            }
            break;
          case FieldNames.classTypesPlural.actualActivities:
            if(!finalArrayForCall.includes(FieldNames.classTypes.actualActivity) && !isActActivitiesLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualActivity}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.actualActivity) && !isActActivityFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualActivity}'`)
            }
            break;
          case FieldNames.classTypes.combinedEstimated:
            if(!finalArrayForCall.includes(FieldNames.classTypes.treatment) && !isTreatmentsLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.treatment}'`)
            }
            if(!finalArrayForCall.includes(FieldNames.classTypes.activity) && !isActivitiesLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.activity}'`)
            }

            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.treatment) && !isTreatmentFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.treatment}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.activity) && !isActivityFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.activity}'`)
            }
            break;
          case FieldNames.classTypes.combinedActual:
            if(!finalArrayForCall.includes(FieldNames.classTypes.actualTreatment) && !isActTreatmentsLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualTreatment}'`)
            }
            if(!finalArrayForCall.includes(FieldNames.classTypes.actualActivity) && !isActActivitiesLoaded){
              finalArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualActivity}'`)
            }

            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.actualTreatment) && !isActTreatmentFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualTreatment}'`)
            }
            if(!finalFundingArrayForCall.includes(FieldNames.classTypes.actualActivity) && !isActActivityFundingLoaded){
              finalFundingArrayForCall.push(`${FieldNames.featureFields.class} = '${FieldNames.classTypes.actualActivity}'`)
            }
            break;
          default:
            break;
        }
      })
      
      //Returning obj since it can have both funding and non-funding selections
      const fundingObj = getIsTypeOfSummary(selSummaries);

      if(fundingObj?.isFunding){
        //Get Funding
        const fundingResponse = await queryAllSummaryFeatureTables(definitionExpression, finalFundingArrayForCall, fundingObj.isFunding).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });
        //Returning everything from funding view
        let treatmentWithFunding = [];
        let activityWithFunding = [];
        let actualTreatmentWithFunding = [];
        let actualActivityWithFunding = [];

        if(selClasses.includes(FieldNames.classTypesPlural.treatments)){
          if(fundingResponse?.length){
            treatmentWithFunding = settingFeatureValues(isTreatmentFundingLoaded, fundingResponse, setTreatmentFundingFeatures, setIsTreatmentFundingLoaded, FieldNames.classTypes.treatment, treatmentFundingFeatures);
          } else {
            treatmentWithFunding = treatmentFundingFeatures;
            setIsTreatmentFundingLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypesPlural.activities)){
          if(fundingResponse?.length){
            activityWithFunding = settingFeatureValues(isActivityFundingLoaded, fundingResponse, setActivityFundingFeatures, setIsActivityFundingLoaded, FieldNames.classTypes.activity, activityFundingFeatures);
          } else {
            activityWithFunding = activityFundingFeatures;
            setIsActivityFundingLoaded(true);
          }
          
        }

        if(selClasses.includes(FieldNames.classTypesPlural.actualTreatments)){
          if(fundingResponse?.length){
            actualTreatmentWithFunding = settingFeatureValues(isActTreatmentFundingLoaded, fundingResponse, setActualTreatmentFundingFeatures, setIsActTreatmentFundingLoaded, FieldNames.classTypes.actualTreatment, actualTreatmentFundingFeatures);
          } else {
            actualTreatmentWithFunding = actualTreatmentFundingFeatures;
            setIsActTreatmentFundingLoaded(true);
          }
          
        }

        if(selClasses.includes(FieldNames.classTypesPlural.actualActivities)){
          if(fundingResponse?.length){
            actualActivityWithFunding = settingFeatureValues(isActActivityFundingLoaded, fundingResponse, setActualActivityFundingFeatures, setIsActActivityFundingLoaded, FieldNames.classTypes.actualActivity, actualActivityFundingFeatures);
          } else {
            actualActivityWithFunding = actualActivityFundingFeatures;
            setIsActActivityFundingLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypes.combinedEstimated) && isTreatmentFundingLoaded && isActivityFundingLoaded){
          let trtFeatures = treatmentWithFunding;
          if(!trtFeatures?.length){
            trtFeatures = treatmentFundingFeatures;
          }
          let actFeatures = activityWithFunding;
          if(!actFeatures?.length){
            actFeatures = activityFundingFeatures;
          }
          setEstimatedCombinedFundingFeatures(trtFeatures.concat(actFeatures))
        } else if(selClasses.includes(FieldNames.classTypes.combinedEstimated) && (!isTreatmentFundingLoaded || !isActivityFundingLoaded)){
          const estCombinedFeatures = gettingCombinedValues(fundingResponse, isTreatmentFundingLoaded, isActivityFundingLoaded, setTreatmentFundingFeatures, setActivityFundingFeatures, setIsTreatmentFundingLoaded, setIsActivityFundingLoaded, FieldNames.classTypes.treatment, FieldNames.classTypes.activity, treatmentFundingFeatures, activityFundingFeatures);
          setEstimatedCombinedFundingFeatures(estCombinedFeatures)
        }

        if(selClasses.includes(FieldNames.classTypes.combinedActual) && isActTreatmentFundingLoaded && isActActivityFundingLoaded){
          let trtFeatures = actualTreatmentWithFunding;
          if(!trtFeatures?.length){
            trtFeatures = actualTreatmentFundingFeatures;
          }
          let actFeatures = actualActivityWithFunding;
          if(!actFeatures?.length){
            actFeatures = actualActivityFundingFeatures;
          }
          setActualCombinedFundingFeatures(trtFeatures.concat(actFeatures))
        } else if(selClasses.includes(FieldNames.classTypes.combinedActual) && (!isActTreatmentFundingLoaded || !isActActivityFundingLoaded)){
          const actCombinedFeatures = gettingCombinedValues(fundingResponse, isActTreatmentFundingLoaded, isActActivityFundingLoaded, setActualTreatmentFundingFeatures, setActualActivityFundingFeatures, setIsActTreatmentFundingLoaded, setIsActActivityFundingLoaded, FieldNames.classTypes.actualTreatment, FieldNames.classTypes.actualActivity, actualTreatmentFundingFeatures, actualActivityFundingFeatures);
          setActualCombinedFundingFeatures(actCombinedFeatures)
        }
      }

      if(fundingObj?.isNonFunding){
        //Get Non Funding
        const featureResponse = await queryAllSummaryFeatureTables(definitionExpression, finalArrayForCall, !fundingObj.isNonFunding).catch(error => {
          ErrorService.addErrorLogEntry(currentUserInfo[FieldNames.userFields.nifcUsername], error);
        });

        //Returning everything from non funding view
        let treatmentWithoutFunding = [];
        let activityWithoutFunding = [];
        let actualTreatmentWithoutFunding = [];
        let actualActivityWithoutFunding = [];

        if(selClasses.includes(FieldNames.classTypesPlural.treatments)){
          if(featureResponse?.length){
            treatmentWithoutFunding = settingFeatureValues(isTreatmentsLoaded, featureResponse, setTreatmentFeatures, setIsTreatmentsLoaded, FieldNames.classTypes.treatment, treatmentFeatures);
          } else {
            treatmentWithoutFunding = treatmentFeatures;
            setIsTreatmentsLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypesPlural.activities)){
          if(featureResponse?.length){
            activityWithoutFunding = settingFeatureValues(isActivitiesLoaded, featureResponse, setActivityFeatures, setIsActivitiesLoaded, FieldNames.classTypes.activity, activityFeatures);
          } else {
            activityWithoutFunding = activityFeatures;
            setIsActivitiesLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypesPlural.actualTreatments)){
          if(featureResponse?.length){
            actualTreatmentWithoutFunding = settingFeatureValues(isActTreatmentsLoaded, featureResponse, setActualTreatmentFeatures, setIsActTreatmentsLoaded, FieldNames.classTypes.actualTreatment, actualTreatmentFeatures);
          } else {
            actualTreatmentWithoutFunding = actualTreatmentFeatures;
            setIsActTreatmentsLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypesPlural.actualActivities)){
          if(featureResponse?.length){
            actualActivityWithoutFunding = settingFeatureValues(isActActivitiesLoaded, featureResponse, setActualActivityFeatures, setIsActActivitiesLoaded, FieldNames.classTypes.actualActivity, actualActivityFeatures);
          } else {
            actualActivityWithoutFunding = actualActivityFeatures;
            setIsActActivitiesLoaded(true);
          }
        }

        if(selClasses.includes(FieldNames.classTypes.combinedEstimated) && isTreatmentsLoaded && isActivitiesLoaded){
          let trtFeatures = treatmentWithoutFunding;
          if(!trtFeatures?.length){
            trtFeatures = treatmentFeatures;
          }
          let actFeatures = activityWithoutFunding;
          if(!actFeatures?.length){
            actFeatures = activityFeatures;
          }
          setEstimatedCombinedFeatures(trtFeatures.concat(actFeatures))
        } else if(selClasses.includes(FieldNames.classTypes.combinedEstimated) && (!isTreatmentsLoaded || !isActivitiesLoaded)){
          const estCombinedFeatures = gettingCombinedValues(featureResponse, isTreatmentsLoaded, isActivitiesLoaded, setTreatmentFeatures, setActivityFeatures, setIsTreatmentsLoaded, setIsActivitiesLoaded, FieldNames.classTypes.treatment, FieldNames.classTypes.activity, treatmentFeatures, activityFeatures);
          setEstimatedCombinedFeatures(estCombinedFeatures)
        }

        if(selClasses.includes(FieldNames.classTypes.combinedActual) && isActTreatmentsLoaded && isActActivitiesLoaded){
          let trtFeatures = actualTreatmentWithoutFunding;
          if(!trtFeatures?.length){
            trtFeatures = actualTreatmentFeatures;
          }
          let actFeatures = actualActivityWithoutFunding;
          if(!actFeatures?.length){
            actFeatures = actualActivityFeatures;
          }
          setActualCombinedFeatures(trtFeatures.concat(actFeatures))
        } else if(selClasses.includes(FieldNames.classTypes.combinedActual) && (!isActTreatmentsLoaded || !isActActivitiesLoaded)){
          const actCombinedFeatures = gettingCombinedValues(featureResponse, isActTreatmentsLoaded, isActActivitiesLoaded, setActualTreatmentFeatures, setActualActivityFeatures, setIsActTreatmentsLoaded, setIsActActivitiesLoaded, FieldNames.classTypes.actualTreatment, FieldNames.classTypes.actualActivity, actualTreatmentFeatures, actualActivityFeatures);
          setActualCombinedFeatures(actCombinedFeatures)
        }
      }
    }
  }

  const handlePrint = useReactToPrint({
    content: () => {
      return componentRef.current
    }
  });

  const getPageHeight = () => {
    if(containerRef && containerRef.current) {
      return containerRef.current?.clientHeight - 97;
    } 
  }

  const isLessThanBaseFilterValues = () => {
    //Regex to verify that there is still 1 or more letters in the definition expression
    let parenthesisRegex = /[abc]+/i;
    let tempDefExpr = definitionExpression;
    tempDefExpr = tempDefExpr.replace(getBaseFilterString(), ""); // Clear out (OBJECTID IS NOT NULL)
    tempDefExpr = tempDefExpr.replace(getBasePostFireString(), "") // Clear out (EntityType = 'PostFire')
    tempDefExpr = tempDefExpr.replace(getBaseFuelsString(), "") // Clear out (EntityType = 'Fuels')
    if(definitionExpression === "" || !parenthesisRegex.test(tempDefExpr)){ //Check to see if definitionExpression is empty or if it doesn't have any more letters
      return true;
    } else {
      return false;
    }
  }

  return (
    <div style={{ height: height, overflow: 'scroll', backgroundColor: ColorConstants.MidGray}}>
      <AppBar
        sx={{ position: "relative" }}
        style={{ backgroundColor: ColorConstants.BlueAlien, marginTop: headerheight }}
        className="no-print-column"
      >
        <Toolbar>
          <FormControl variant="standard" className={classes.formControl}>
            <ThemeProvider theme={theme}>
              <Grid
                item
                size={12}
                container
                direction="row"
                className="grid-item"
                style={{ alignItems: "center" }}
              >
                <Grid item size={0.2}>
                  <IconButton
                    edge="start"
                    color="inherit"
                    onClick={handleClose}
                    aria-label="close"
                    className="no-print-column"
                  >
                    <CloseIcon style={{ fill: ColorConstants.WhiteAlien }} />
                  </IconButton>
                </Grid>
                <Grid item size={11.8} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <Typography
                      sx={{ ml: 2, flex: 1 }}
                      variant="h6"
                      component="div"
                      style={{ color: ColorConstants.WhiteAlien }}
                      className="no-print-column"
                    >
                      Reporting
                    </Typography>
                    <BootstrapButton
                      color="primary"
                      className="reporting-print-btn"
                      variant="contained" 
                      size="small"
                      disabled={(selectedSummaries.length === 0 || selectedClasses.length === 0)}
                      onClick={() => {
                        if(isPrinting){
                          setIsPrinting(false);
                        } else {
                          setIsPrinting(true);
                        }
                      }
                    }
                    >
                      {isPrinting ? `Summaries` : `Print`}
                    </BootstrapButton>
                    {isPrinting && 
                      <BootstrapButton
                        variant="contained"
                        id="printBackButton"
                        
                        style={{width: "100px", marginLeft: 5}}
                        onClick={() => {
                          handlePrint()
                        }}
                      >Print</BootstrapButton>
                    }
                    
                </Grid>
              </Grid>
            </ThemeProvider>
          </FormControl></Toolbar>
      </AppBar>
      <Paper>
        <Box
          sx={{ height: "auto", width: "100%", minWidth: "10px",
          }}
        >
        {(isPrinting) ? (
          <ComponentToPrint 
            key="SummariesList"
            summaries={selectedSummaries ? selectedSummaries : []}
            activityFeatures={activityFeatures}
            treatmentFeatures={treatmentFeatures}
            estimatedCombinedFeatures={estimatedCombinedFeatures}
            definitionExpression={definitionExpression}
            selectedClasses={selectedClasses}
            actualActivityFeatures={actualActivityFeatures}
            actualTreatmentFeatures={actualTreatmentFeatures}
            actualCombinedFeatures={actualCombinedFeatures}
            isPrinting={isPrinting}
            treatmentFundingFeatures={treatmentFundingFeatures}
            activityFundingFeatures={activityFundingFeatures}
            actualTreatmentFundingFeatures={actualTreatmentFundingFeatures}
            actualActivityFundingFeatures={actualActivityFundingFeatures}
            estimatedCombinedFundingFeatures={estimatedCombinedFundingFeatures}
            actualCombinedFundingFeatures={actualCombinedFundingFeatures}
            isTreatmentsLoaded={isTreatmentsLoaded}
            isActivitiesLoaded={isActivitiesLoaded}
            isActTreatmentsLoaded={isActTreatmentsLoaded}
            isActActivitiesLoaded={isActActivitiesLoaded}
            isCheckboxChanged={isCheckboxChanged}
            isTreatmentFundingLoaded={isTreatmentFundingLoaded} 
            isActivityFundingLoaded={isActivityFundingLoaded} 
            isActActivityFundingLoaded={isActActivityFundingLoaded} 
            isActTreatmentFundingLoaded={isActTreatmentFundingLoaded}
            handlePrint={handlePrint}
            ref={componentRef} 
        />
      ) : (
          <Grid container spacing={1} sx={{ position: 'relative' }} className="content" style={{ height: (getPageHeight() - 12), backgroundColor: ColorConstants.LightGrayAlien}}>
            <Grid item size={3} style={{ paddingTop: 0 }} className="no-print-column">
              <Paper style={{ width: '100%', paddingTop: 10, paddingLeft: 5 }}>
                <Tooltip title={(currentUserInfo && currentUserInfo[FieldNames.userFields.isElevatedSummaries] === FieldNames.yesNo.yes) ? '' : isLessThanBaseFilterValues() ? 'Filters must be applied to create summaries' : ''} enterDelay={400}>
                  <span>
                    <Button
                      primary="true"
                      color="primary"
                      disabled={(selectedClasses?.length === 0 || selectedSummaries?.length === 0 || isLessThanBaseFilterValues()) ? true : false}
                      style={{ display: 'block', marginTop: 10 }}
                      variant="contained"
                      onClick={async() => await handleGenerateSummaries(selectedClasses, selectedSummaries)}
                    >Create Summaries</Button>
                  </span>
                </Tooltip>
                <Typography sx={{color: 'red', }}>Warning: At National View this may take up to several minutes to retrieve data.</Typography>
                <Grid
                    item
                    size={12}
                    container
                    direction="row"
                    className="grid-item"
                    style={{ alignItems: "center" }}
                  >
                    <Grid item size={10}>
                      <Box
                        sx={{ backgroundColor: "#D3D3D3", marginBottom: "10px" }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <HelpIcon
                          fontSize="small"
                          color="primary"
                          style={{ paddingRight: "5px" }}
                        />
                        <Typography fontSize={12}>
                          Note: Review filters as they do apply on summaries
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                <h2>Available Class</h2>
                <List className={classes.root}>
                  <SummarySelectionMenu summaryOptions={summaryClassItems} selectedItems={selectedClasses} setSelectedItems={setSelectedClasses} setIsCheckboxChanged={setIsCheckboxChanged} />
                </List>
                <h2>Available Summaries</h2>
                <List className={classes.root}>
                  <SummarySelectionMenu summaryOptions={summaryOptionItems} selectedItems={selectedSummaries} setSelectedItems={setSelectedSummaries} setIsCheckboxChanged={setIsCheckboxChanged} />
                </List>
                <Tooltip title={(currentUserInfo && currentUserInfo[FieldNames.userFields.isElevatedSummaries] === FieldNames.yesNo.yes) ? '' : isLessThanBaseFilterValues() ? 'Filters must be applied to create summaries' : ''} enterDelay={400}>
                  <span>
                    <Button
                      primary="true"
                      color="primary"
                      disabled={(selectedClasses?.length === 0 || selectedSummaries?.length === 0 || isLessThanBaseFilterValues()) ? true : false}
                      style={{ display: 'block', marginBottom: 10 }}
                      variant="contained"
                      onClick={async() => await handleGenerateSummaries(selectedClasses, selectedSummaries)}
                    >Create Summaries</Button>
                  </span>
                </Tooltip>
                <Typography sx={{color: 'red'}}>Warning: At National View this may take up to several minutes to retrieve data.</Typography>
              </Paper>
            </Grid>
             
            <Grid item size={9} style={{ height: (getPageHeight() - 12)}} >
              {(isLoading) ? 
                (<Stack alignItems="center" style={{ display: 'flex', justifyContent: 'center', verticalAlign: 'middle', height: height, width: '100%' }}><CircularProgress size={200} thickness={3} color={theme.palette.primary1Color} /></Stack>) : 
                ( !isCheckboxChanged &&
                  <Grid item size={12} className="print-column">
                    {/* <div className="report-page">
                    </div> */}
                      <SummariesList
                      key="SummariesList"
                      summaries={selectedSummaries ? selectedSummaries : []}
                      activityFeatures={activityFeatures}
                      treatmentFeatures={treatmentFeatures}
                      estimatedCombinedFeatures={estimatedCombinedFeatures}
                      definitionExpression={definitionExpression}
                      selectedClasses={selectedClasses}
                      actualActivityFeatures={actualActivityFeatures}
                      actualTreatmentFeatures={actualTreatmentFeatures}
                      actualCombinedFeatures={actualCombinedFeatures}
                      isPrinting={false}
                      treatmentFundingFeatures={treatmentFundingFeatures}
                      activityFundingFeatures={activityFundingFeatures}
                      actualTreatmentFundingFeatures={actualTreatmentFundingFeatures}
                      actualActivityFundingFeatures={actualActivityFundingFeatures}
                      estimatedCombinedFundingFeatures={estimatedCombinedFundingFeatures}
                      actualCombinedFundingFeatures={actualCombinedFundingFeatures}
                      isTreatmentsLoaded={isTreatmentsLoaded}
                      isActivitiesLoaded={isActivitiesLoaded}
                      isActTreatmentsLoaded={isActTreatmentsLoaded}
                      isActActivitiesLoaded={isActActivitiesLoaded}
                      isCheckboxChanged={isCheckboxChanged}
                      isTreatmentFundingLoaded={isTreatmentFundingLoaded} 
                      isActivityFundingLoaded={isActivityFundingLoaded} 
                      isActActivityFundingLoaded={isActActivityFundingLoaded} 
                      isActTreatmentFundingLoaded={isActTreatmentFundingLoaded}
                    />
                  </Grid>
                )
              }
            </Grid>
    
          </Grid>
      )}
        </Box>
      </Paper>
    </div>

  )
  
}
